import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { GetUsersResponse } from '../../types/Admin.api.type';

const ROWS_PER_PAGE = 20;
const PLATFORM_TABS = ['전체', 'KAKAO', 'GOOGLE', 'TOSS'] as const;

export const LoginHistoryManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [visibleRows, setVisibleRows] = useState(10);

  const { data: allUsers = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const response = await adminApi.getUsers();
      return response.data;
    },
  });

  // 플랫폼 필터 + 검색 + 최신순 정렬
  const filteredUsers = useMemo(() => {
    let result = [...allUsers];

    // 플랫폼 필터
    if (activeTab !== '전체') {
      result = result.filter(
        (u) => u.socialPlatform?.toUpperCase() === activeTab
      );
    }

    // 검색 (닉네임, 이메일)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (u) =>
          u.userName?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q)
      );
    }

    // 최신순 정렬
    result.sort(
      (a, b) =>
        new Date(b.regDateTime).getTime() - new Date(a.regDateTime).getTime()
    );

    return result;
  }, [allUsers, activeTab, searchQuery]);

  // 페이징
  const totalPages = Math.ceil(filteredUsers.length / ROWS_PER_PAGE);
  const pagedUsers = filteredUsers.slice(
    currentPage * ROWS_PER_PAGE,
    (currentPage + 1) * ROWS_PER_PAGE
  );
  const mobileData = pagedUsers.slice(0, visibleRows);

  // 플랫폼별 카운트
  const getTabCount = (tab: string) => {
    if (tab === '전체') return allUsers.length;
    return allUsers.filter(
      (u) => u.socialPlatform?.toUpperCase() === tab
    ).length;
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setVisibleRows(10);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
    setVisibleRows(10);
  };

  const getPlatformBadgeStyles = (platform: string) => {
    switch (platform?.toUpperCase()) {
      case 'GOOGLE':
        return 'bg-[#4285F4] text-white';
      case 'KAKAO':
        return 'bg-[#FEE500] text-[#000000]';
      case 'TOSS':
        return 'bg-[#0064FF] text-white';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatPlatformLabel = (platform: string) => {
    switch (platform?.toUpperCase()) {
      case 'GOOGLE':
        return 'Google';
      case 'KAKAO':
        return 'Kakao';
      case 'TOSS':
        return 'Toss';
      default:
        return platform || '-';
    }
  };

  if (isLoading) {
    return <div className="p-6 text-center">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header + Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg lg:text-xl font-bold text-[#030213]">가입 내역</h2>
            <span className="text-sm text-[#6B7280]">
              총 {filteredUsers.length.toLocaleString()}명
            </span>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="닉네임 또는 이메일로 검색..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#030213] focus:border-transparent"
            />
          </div>
        </div>

        {/* Platform Tabs */}
        <div className="flex gap-2 mt-4 border-b border-[#E5E7EB] overflow-x-auto">
          {PLATFORM_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-4 py-2 text-sm font-medium transition-colors relative whitespace-nowrap ${
                activeTab === tab
                  ? 'text-[#030213]'
                  : 'text-[#6B7280] hover:text-[#030213]'
              }`}
            >
              {tab}
              <span className="ml-1 text-xs text-[#9CA3AF]">
                ({getTabCount(tab).toLocaleString()})
              </span>
              {activeTab === tab && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#030213]" />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#f3f3f5]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                사용자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                이메일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                가입 플랫폼
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                가입일
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                최근 로그인
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                투표여부
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">
                상태
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-[#E5E7EB]">
            {pagedUsers.map((user) => (
              <UserRow key={user.userId} user={user} getPlatformBadgeStyles={getPlatformBadgeStyles} formatPlatformLabel={formatPlatformLabel} />
            ))}
            {pagedUsers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-[#6B7280]">
                  가입 내역이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-sm text-[#6B7280]">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
              }
              disabled={currentPage >= totalPages - 1}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {mobileData.map((user) => (
          <MobileUserCard key={user.userId} user={user} getPlatformBadgeStyles={getPlatformBadgeStyles} formatPlatformLabel={formatPlatformLabel} />
        ))}

        {pagedUsers.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm p-6 text-center text-[#6B7280]">
            가입 내역이 없습니다.
          </div>
        )}

        {/* Load More Button */}
        {visibleRows < pagedUsers.length && (
          <button
            onClick={() => setVisibleRows((prev) => Math.min(prev + 10, pagedUsers.length))}
            className="w-full py-3 text-sm font-medium text-[#030213] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] transition-colors"
          >
            더 보기
          </button>
        )}

        {/* Mobile Pagination */}
        {totalPages > 1 && visibleRows >= pagedUsers.length && (
          <div className="flex justify-center items-center gap-2 py-4">
            <button
              onClick={() => {
                setCurrentPage((prev) => Math.max(0, prev - 1));
                setVisibleRows(10);
              }}
              disabled={currentPage === 0}
              className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              이전 페이지
            </button>
            <span className="text-sm text-[#6B7280]">
              {currentPage + 1} / {totalPages}
            </span>
            <button
              onClick={() => {
                setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
                setVisibleRows(10);
              }}
              disabled={currentPage >= totalPages - 1}
              className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              다음 페이지
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/** Desktop table row */
const UserRow: React.FC<{
  user: GetUsersResponse;
  getPlatformBadgeStyles: (p: string) => string;
  formatPlatformLabel: (p: string) => string;
}> = ({ user, getPlatformBadgeStyles, formatPlatformLabel }) => (
  <tr className="hover:bg-[#f3f3f5] transition-colors">
    <td className="px-6 py-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#f3f3f5] flex items-center justify-center">
          {user.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt={user.userName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <span className="text-[#6B7280] font-medium">
              {user.userName?.charAt(0)?.toUpperCase() || '?'}
            </span>
          )}
        </div>
        <span className="text-sm font-medium text-[#030213]">{user.userName}</span>
      </div>
    </td>
    <td className="px-6 py-4 text-sm text-[#6B7280]">{user.email || '-'}</td>
    <td className="px-6 py-4 whitespace-nowrap">
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPlatformBadgeStyles(user.socialPlatform)}`}>
        {formatPlatformLabel(user.socialPlatform)}
      </span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
      {new Date(user.regDateTime).toLocaleString('ko-KR')}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6B7280]">
      {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('ko-KR') : '-'}
    </td>
    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
      <span className={user.hasVoted ? 'text-blue-600' : 'text-[#9CA3AF]'}>
        {user.hasVoted ? 'O' : 'X'}
      </span>
    </td>
    <td className="px-6 py-4 whitespace-nowrap">
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
        user.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        {user.status === 'ACTIVE' ? '활성' : '비활성'}
      </span>
    </td>
  </tr>
);

/** Mobile card */
const MobileUserCard: React.FC<{
  user: GetUsersResponse;
  getPlatformBadgeStyles: (p: string) => string;
  formatPlatformLabel: (p: string) => string;
}> = ({ user, getPlatformBadgeStyles, formatPlatformLabel }) => (
  <div className="bg-white rounded-lg shadow-sm p-4">
    <div className="flex items-start gap-3">
      <div className="w-12 h-12 rounded-full bg-[#f3f3f5] flex items-center justify-center">
        {user.profileImageUrl ? (
          <img
            src={user.profileImageUrl}
            alt={user.userName}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <span className="text-[#6B7280] font-medium text-lg">
            {user.userName?.charAt(0)?.toUpperCase() || '?'}
          </span>
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-[#030213]">{user.userName}</span>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPlatformBadgeStyles(user.socialPlatform)}`}>
            {formatPlatformLabel(user.socialPlatform)}
          </span>
        </div>
        <p className="text-xs text-[#6B7280] mb-1">{user.email || '-'}</p>
        <div className="flex items-center justify-between">
          <p className="text-xs text-[#6B7280]">
            가입: {new Date(user.regDateTime).toLocaleString('ko-KR')}
          </p>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${user.hasVoted ? 'text-blue-600' : 'text-[#9CA3AF]'}`}>
              투표 {user.hasVoted ? 'O' : 'X'}
            </span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
              user.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {user.status === 'ACTIVE' ? '활성' : '비활성'}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);
